import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import PhotoCapture from "@/components/PhotoCapture";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, KeyRound, Power, Loader2, Users, Trash2, Pencil, Printer, CheckSquare, UsersRound, Image as ImageIcon, QrCode } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { BulkStudentDialog } from "@/components/BulkStudentDialog";
import { useAuth } from "@/context/AuthContext";

const KELAS_LABEL = (s) => `Kelas ${s.kelas}${s.rombel || ""}`;

export default function Students() {
  const { user } = useAuth();
  const wali = user?.teacher_type === "wali" && user?.wali_kelas;
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(wali ? String(user.wali_kelas) : "all");
  const [rombelFilter, setRombelFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", kelas: wali ? String(user.wali_kelas) : "1", rombel: wali ? (user.wali_rombel || "") : "", username: "", password: "", photo_path: "" });
  const [saving, setSaving] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [delFor, setDelFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", kelas: "1", rombel: "", username: "" });
  const [bulkOpen, setBulkOpen] = useState(false);

  const openEdit = (s) => {
    setEditFor(s);
    setEditForm({ name: s.name, kelas: String(s.kelas), rombel: s.rombel || "", username: s.username || "" });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return toast.error("Nama wajib diisi");
    if (!editForm.username.trim()) return toast.error("Username wajib diisi");
    setSaving(true);
    try {
      await api.put(`/students/${editFor.id}`, { name: editForm.name.trim(), kelas: parseInt(editForm.kelas), rombel: editForm.rombel.trim().toUpperCase(), username: editForm.username.trim().toLowerCase() });
      toast.success("Data murid diperbarui");
      setEditFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memperbarui murid");
    } finally {
      setSaving(false);
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/students").then((r) => setStudents(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.username || !form.password) {
      toast.error("Nama, username, dan kata sandi wajib diisi");
      return;
    }
    setSaving(true);
    try {
      await api.post("/students", { ...form, kelas: parseInt(form.kelas), rombel: form.rombel.trim().toUpperCase() });
      toast.success("Murid berhasil ditambahkan");
      setOpen(false);
      setForm({ name: "", kelas: wali ? String(user.wali_kelas) : "1", rombel: wali ? (user.wali_rombel || "") : "", username: "", password: "", photo_path: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menambah murid");
    } finally {
      setSaving(false);
    }
  };

  const savePw = async () => {
    if (newPw.length < 4) return toast.error("Kata sandi minimal 4 karakter");
    setSaving(true);
    try {
      await api.put(`/students/${pwFor.id}/password`, { new_password: newPw });
      toast.success(`Kata sandi ${pwFor.name} diubah menjadi: ${newPw}`, { duration: 8000 });
      setPwFor(null);
      setNewPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengubah kata sandi");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/students/${delFor.id}`);
      toast.success(`${delFor.name} dihapus`);
      setDelFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus murid");
    }
  };

  const toggle = async (id, active) => {
    await api.put(`/students/${id}/active?active=${!active}`);
    toast.success(active ? "Akun dinonaktifkan" : "Akun diaktifkan");
    load();
  };

  const [printing, setPrinting] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const printCards = async (format = "pdf") => {
    setPrinting(format);
    try {
      const params = { format };
      if (selectMode && selected.length) params.ids = selected.join(",");
      else {
        if (filter !== "all") params.kelas = filter;
        if (rombelFilter !== "all") params.rombel = rombelFilter;
      }
      const res = await api.get("/students/access-cards", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const who = params.ids ? `${selected.length}_Murid` : (filter === "all" ? "Semua" : `Kelas_${filter}${rombelFilter === "all" ? "" : rombelFilter}`);
      const ext = format === "jpg" ? (res.data.type === "application/zip" ? "zip" : "jpg") : "pdf";
      a.href = url;
      a.download = `Kartu_Akses_${who}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Kartu akses diunduh (${format.toUpperCase()}${ext === "zip" ? " dalam ZIP" : ""})`);
    } catch (e) {
      toast.error("Gagal membuat kartu akses");
    } finally {
      setPrinting(false);
    }
  };

  const [qrPrinting, setQrPrinting] = useState(false);
  const printQrCards = async (format = "pdf") => {
    setQrPrinting(format);
    try {
      const params = { format };
      if (selectMode && selected.length) params.ids = selected.join(",");
      else {
        if (filter !== "all") params.kelas = filter;
        if (rombelFilter !== "all") params.rombel = rombelFilter;
      }
      const res = await api.get("/students/qr-cards", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const who = params.ids ? `${selected.length}_Murid` : (filter === "all" ? "Semua" : `Kelas_${filter}${rombelFilter === "all" ? "" : rombelFilter}`);
      const ext = format === "jpg" ? (res.data.type === "application/zip" ? "zip" : "jpg") : "pdf";
      a.href = url;
      a.download = `Kartu_QR_${who}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Kartu QR absensi diunduh (${format.toUpperCase()}${ext === "zip" ? " dalam ZIP" : ""})`);
    } catch (e) {
      toast.error("Gagal membuat kartu QR absensi");
    } finally {
      setQrPrinting(false);
    }
  };

  const byKelas = filter === "all" ? students : students.filter((s) => String(s.kelas) === filter);
  const rombels = [...new Set(byKelas.map((s) => s.rombel).filter(Boolean))].sort();
  const shown = rombelFilter === "all" ? byKelas : byKelas.filter((s) => (s.rombel || "") === rombelFilter);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-800">Kelola Murid</h1>
          <p className="text-slate-500 mt-1">{wali ? `Murid kelas ${user.wali_kelas}${user.wali_rombel || ""} yang kamu pegang.` : "Tambah dan atur akun murid kelas 1–6 beserta rombel (1A, 1B, dst.)."}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant={selectMode ? "default" : "outline"} className={`rounded-xl h-11 ${selectMode ? "bg-indigo-600 hover:bg-indigo-700" : ""}`} onClick={() => { setSelectMode((m) => !m); setSelected([]); }} data-testid="select-mode-btn">
            <CheckSquare className="w-5 h-5 mr-2" /> {selectMode ? "Selesai Memilih" : "Pilih Murid"}
          </Button>
          <Button variant="outline" className="rounded-xl h-11" onClick={() => printCards("pdf")} disabled={!!printing || (selectMode ? selected.length === 0 : shown.length === 0)} data-testid="print-access-cards-pdf-btn">
            {printing === "pdf" ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Printer className="w-5 h-5 mr-2" />} Kartu Akses PDF{selectMode && selected.length > 0 ? ` (${selected.length})` : ""}
          </Button>
          <Button variant="outline" className="rounded-xl h-11 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => printCards("jpg")} disabled={!!printing || (selectMode ? selected.length === 0 : shown.length === 0)} data-testid="print-access-cards-jpg-btn">
            {printing === "jpg" ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ImageIcon className="w-5 h-5 mr-2" />} Kartu Akses JPG{selectMode && selected.length > 0 ? ` (${selected.length})` : ""}
          </Button>
          <Button variant="outline" className="rounded-xl h-11 border-teal-300 text-teal-700 hover:bg-teal-50" onClick={() => printQrCards("pdf")} disabled={!!qrPrinting || (selectMode ? selected.length === 0 : shown.length === 0)} data-testid="print-qr-cards-pdf-btn">
            {qrPrinting === "pdf" ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <QrCode className="w-5 h-5 mr-2" />} Kartu QR PDF{selectMode && selected.length > 0 ? ` (${selected.length})` : ""}
          </Button>
          <Button variant="outline" className="rounded-xl h-11 border-teal-300 text-teal-700 hover:bg-teal-50" onClick={() => printQrCards("jpg")} disabled={!!qrPrinting || (selectMode ? selected.length === 0 : shown.length === 0)} data-testid="print-qr-cards-jpg-btn">
            {qrPrinting === "jpg" ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <QrCode className="w-5 h-5 mr-2" />} Kartu QR JPG{selectMode && selected.length > 0 ? ` (${selected.length})` : ""}
          </Button>
          <Button variant="outline" className="rounded-xl h-11 border-teal-300 text-teal-700 hover:bg-teal-50" onClick={() => setBulkOpen(true)} data-testid="add-bulk-student-btn">
            <UsersRound className="w-5 h-5 mr-2" /> Tambah Massal
          </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl h-11 bg-teal-600 hover:bg-teal-700" data-testid="add-student-btn">
              <UserPlus className="w-5 h-5 mr-2" /> Tambah Murid
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Tambah Murid Baru</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" data-testid="student-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Kelas</Label>
                  <Select value={form.kelas} onValueChange={(v) => setForm({ ...form, kelas: v })} disabled={!!wali}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="student-kelas-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5, 6].map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Rombel (A/B/…)</Label>
                  <Input value={form.rombel} onChange={(e) => setForm({ ...form, rombel: e.target.value })} placeholder="A" maxLength={3} disabled={!!(wali && user.wali_rombel)} className="mt-1 rounded-xl uppercase" data-testid="student-rombel-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 rounded-xl" data-testid="student-username-input" /></div>
                <div><Label>Kata Sandi Awal</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-xl" data-testid="student-password-input" /></div>
              </div>
              <div>
                <Label>Foto Profil (opsional)</Label>
                <div className="mt-2"><PhotoCapture category="profile" testIdPrefix="student-photo" label="Ambil Foto" onUploaded={(path) => { setForm({ ...form, photo_path: path }); }} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="save-student-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {!wali && (
        <div className="flex gap-2 flex-wrap">
          <FilterChip active={filter === "all"} onClick={() => { setFilter("all"); setRombelFilter("all"); }}>Semua</FilterChip>
          {[1, 2, 3, 4, 5, 6].map((k) => (
            <FilterChip key={k} active={filter === String(k)} onClick={() => { setFilter(String(k)); setRombelFilter("all"); }}>Kelas {k}</FilterChip>
          ))}
        </div>
      )}
      {rombels.length > 0 && (
        <div className="flex gap-2 flex-wrap items-center" data-testid="rombel-filter">
          <span className="text-xs text-slate-500 mr-1">Rombel:</span>
          <FilterChip active={rombelFilter === "all"} onClick={() => setRombelFilter("all")} small>Semua</FilterChip>
          {rombels.map((r) => (
            <FilterChip key={r} active={rombelFilter === r} onClick={() => setRombelFilter(r)} small testId={`rombel-chip-${r}`}>{filter === "all" ? r : `${filter}${r}`}</FilterChip>
          ))}
        </div>
      )}

      {selectMode && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl px-4 py-3 flex flex-wrap items-center gap-3 text-sm text-indigo-800" data-testid="select-bar">
          <span className="font-semibold" data-testid="selected-count">{selected.length} murid dipilih</span>
          <button className="text-xs underline" onClick={() => setSelected(shown.map((s) => s.id))} data-testid="select-all-btn">Pilih semua yang tampil</button>
          <button className="text-xs underline" onClick={() => setSelected([])} data-testid="select-none-btn">Kosongkan</button>
          <span className="text-xs text-indigo-600 ml-auto">Klik kartu murid untuk memilih, lalu tekan "Kartu Akses".</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      ) : shown.length === 0 ? (
        <Empty />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {shown.map((s) => (
            <div key={s.id} onClick={selectMode ? () => toggleSelect(s.id) : undefined} className={`relative bg-white rounded-2xl p-5 border shadow-sm transition-colors ${selectMode ? "cursor-pointer" : ""} ${selectMode && selected.includes(s.id) ? "border-indigo-400 ring-2 ring-indigo-200" : s.active ? "border-slate-100" : "border-rose-100 opacity-70"}`} data-testid={`student-card-${s.id}`}>
              {selectMode && (
                <span className={`absolute top-3 right-3 w-6 h-6 rounded-md border-2 flex items-center justify-center ${selected.includes(s.id) ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 bg-white"}`} data-testid={`select-check-${s.id}`}>
                  {selected.includes(s.id) && <CheckSquare className="w-4 h-4" />}
                </span>
              )}
              <div className="flex items-center gap-3">
                {s.photo_path ? (
                  <img src={fileUrl(s.photo_path)} alt={s.name} className="w-12 h-12 rounded-full object-cover border-2 border-teal-100" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-display font-semibold">{s.name[0]}</div>
                )}
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{s.name}</p>
                  <p className="text-xs text-slate-500">{KELAS_LABEL(s)} · @{s.username}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 text-xs">
                <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 font-medium">⭐ {s.points || 0}</span>
                {!s.active && <span className="px-2 py-1 rounded-full bg-rose-50 text-rose-700">Nonaktif</span>}
              </div>
              {!selectMode && <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="rounded-lg px-2.5" onClick={() => openEdit(s)} data-testid={`edit-student-${s.id}`} aria-label="Edit murid">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg flex-1" onClick={() => { setPwFor(s); setNewPw(""); }} data-testid={`set-pw-${s.id}`}>
                  <KeyRound className="w-4 h-4 mr-1" /> Sandi
                </Button>
                <Button size="sm" variant="outline" className={`rounded-lg flex-1 ${s.active ? "text-rose-600" : "text-emerald-600"}`} onClick={() => toggle(s.id, s.active)} data-testid={`toggle-active-${s.id}`}>
                  <Power className="w-4 h-4 mr-1" /> {s.active ? "Nonaktif" : "Aktif"}
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg text-rose-600 px-2.5" onClick={() => setDelFor(s)} data-testid={`delete-student-${s.id}`} aria-label="Hapus murid">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editFor} onOpenChange={(v) => !v && setEditFor(null)}>
        <DialogContent className="rounded-2xl" data-testid="edit-student-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit Data Murid</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Lengkap</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 rounded-xl" data-testid="edit-student-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kelas</Label>
                <Select value={editForm.kelas} onValueChange={(v) => setEditForm({ ...editForm, kelas: v })} disabled={!!wali}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="edit-student-kelas-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{[1, 2, 3, 4, 5, 6].map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rombel (A/B/…)</Label>
                <Input value={editForm.rombel} onChange={(e) => setEditForm({ ...editForm, rombel: e.target.value })} placeholder="A" maxLength={3} className="mt-1 rounded-xl uppercase" data-testid="edit-student-rombel-input" />
              </div>
            </div>
            <div>
              <Label>Username (untuk login)</Label>
              <Input value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} className="mt-1 rounded-xl lowercase" data-testid="edit-student-username-input" />
              <p className="text-xs text-slate-500 mt-1">Huruf kecil tanpa spasi. Pastikan belum dipakai murid lain.</p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} disabled={saving} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="save-edit-student-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan Perubahan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwFor} onOpenChange={(v) => !v && setPwFor(null)}>
        <DialogContent className="rounded-2xl" data-testid="set-password-dialog">
          <DialogHeader><DialogTitle className="font-display">Atur Kata Sandi {pwFor?.name}</DialogTitle></DialogHeader>
          <div>
            <Label>Kata Sandi Baru</Label>
            <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Ketik kata sandi baru" className="mt-1 rounded-xl" data-testid="student-new-password-input" />
            <p className="text-xs text-slate-500 mt-2">Catat dan sampaikan kata sandi ini kepada murid.</p>
          </div>
          <DialogFooter>
            <Button onClick={savePw} disabled={saving} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="save-student-password-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan Sandi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkStudentDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        wali={!!wali}
        defaultKelas={wali ? String(user.wali_kelas) : (filter !== "all" ? filter : "1")}
        defaultRombel={wali ? (user.wali_rombel || "") : ""}
        onDone={load}
      />

      <ConfirmDialog
        open={!!delFor}
        onOpenChange={(v) => !v && setDelFor(null)}
        title={`Hapus ${delFor?.name}?`}
        description="Akun murid beserta seluruh tugas, nilai, dan absensinya akan dihapus permanen."
        onConfirm={remove}
        testId="delete-student-dialog"
      />
    </div>
  );
}

const FilterChip = ({ active, onClick, children, small, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`${small ? "px-3 py-1 text-xs" : "px-4 py-2 text-sm"} rounded-full font-medium transition-colors ${active ? "bg-teal-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-teal-300"}`}>{children}</button>
);

const Empty = () => (
  <div className="text-center py-20 text-slate-400">
    <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
    <p>Belum ada murid. Tambahkan murid pertamamu!</p>
  </div>
);
